// machineFilterStore.js

import { defineStore } from "pinia";
import { useMachineStore } from "@/stores/useMachineStore";

// in Millisekunden
const TTL_TIME = 300 * 60 * 1000; // 5 Stunden

/**
 * Speichert einen Wert mit Ablaufzeit (TTL) in den LocalStorage.
 * @param {string} key - Schlüssel (Name) für den LocalStorage.
 * @param {any} value - Zu speichernder Wert.
 * @param {number} ttl - Time to live (in Millisekunden).
 */
function setItemWithExpiry(key, value, ttl) {
  const now = new Date();
  const item = {
    value: value,
    expiry: now.getTime() + ttl,
  };
  localStorage.setItem(key, JSON.stringify(item));
}

/**
 * Holt einen Wert aus dem LocalStorage und prüft, ob er noch gültig ist.
 * @param {string} key - Schlüssel (Name) für den LocalStorage.
 * @returns {any|null} - Den gespeicherten Wert oder null, wenn abgelaufen/nicht vorhanden.
 */
function getItemWithExpiry(key) {
  const itemStr = localStorage.getItem(key);
  if (!itemStr) return null;

  try {
    const item = JSON.parse(itemStr);
    const now = new Date();
    if (now.getTime() > item.expiry) {
      localStorage.removeItem(key);
      return null;
    }
    return item.value;
  } catch (err) {
    // Falls das JSON fehlerhaft ist, entfernen wir den Eintrag
    localStorage.removeItem(key);
    return null;
  }
}

export const useMachineFilterStore = defineStore("machineFilter", {
  state: () => ({
    // Beim Laden aus LocalStorage beachten wir, dass die Funktion null zurückliefern kann:
    // => Falls null, Standardwert nehmen.
    selectedMachineType: getItemWithExpiry("selectedMachineType") ?? "",
    selectedEngineType: getItemWithExpiry("selectedEngineType") ?? "",
    selectedManufacturer: getItemWithExpiry("selectedManufacturer") ?? "",
    selectedTocGroup: getItemWithExpiry("selectedTocGroup") ?? "",

    capacityRange: getItemWithExpiry("capacityRange")
      ? JSON.parse(getItemWithExpiry("capacityRange"))
      : [0, 10000],

    yocRange: getItemWithExpiry("yocRange")
      ? JSON.parse(getItemWithExpiry("yocRange"))
      : [1950, new Date().getFullYear()],

    priceRange: getItemWithExpiry("priceRange")
      ? JSON.parse(getItemWithExpiry("priceRange"))
      : [0, 100000],
  }),

  getters: {
    /**
     * Maschinen-Filterung aus dem machineStore
     * anhand der oben im State gesetzten Filterwerte
     */
    filteredMachines: (state) => {
      const machineStore = useMachineStore();
      const allMachines = machineStore.machines;

      return allMachines.filter((machine) => {
        // 1) Maschinentyp
        if (
          state.selectedMachineType &&
          String(machine.machine_typ) !== String(state.selectedMachineType)
        ) {
          return false;
        }

        // 2) toc_group_l
        if (
          state.selectedTocGroup &&
          machine.toc_group_l !== state.selectedTocGroup
        ) {
          return false;
        }

        // 3) Antriebsart
        if (
          state.selectedEngineType &&
          machine.enginetype_l !== state.selectedEngineType
        ) {
          return false;
        }

        // 4) Hersteller
        if (
          state.selectedManufacturer &&
          machine.manufacturer !== state.selectedManufacturer
        ) {
          return false;
        }

        // 5) Kapazität (capacity)
        const cVal = +machine.capacity;
        if (cVal < state.capacityRange[0] || cVal > state.capacityRange[1]) {
          return false;
        }

        // 6) Baujahr (yoc)
        const yVal = +machine.yoc;
        if (yVal < state.yocRange[0] || yVal > state.yocRange[1]) {
          return false;
        }

        // 7) Preis
        const pVal = +machine.custprice;
        if (pVal < state.priceRange[0] || pVal > state.priceRange[1]) {
          return false;
        }

        return true;
      });
    },

    // Dynamische Ausgangswerte aus dem Maschinenstore:
    capacityAllMin() {
      const machineStore = useMachineStore();
      if (!machineStore.machines.length) return 0;
      const caps = machineStore.machines.map((m) => +m.capacity || 0);
      return Math.min(...caps);
    },
    capacityAllMax() {
      const machineStore = useMachineStore();
      if (!machineStore.machines.length) return 10000;
      const caps = machineStore.machines.map((m) => +m.capacity || 0);
      return Math.max(...caps);
    },
    yocAllMin() {
      const machineStore = useMachineStore();
      if (!machineStore.machines.length) return 1950;
      const years = machineStore.machines.map((m) => +m.yoc || 0);
      return Math.max(1950, Math.min(...years));
    },
    yocAllMax() {
      const machineStore = useMachineStore();
      if (!machineStore.machines.length) return new Date().getFullYear();
      const years = machineStore.machines.map((m) => +m.yoc || 0);
      return Math.max(...years);
    },
    priceAllMin() {
      const machineStore = useMachineStore();
      if (!machineStore.machines.length) return 0;
      const prices = machineStore.machines.map((m) => +m.custprice || 0);
      return Math.min(...prices);
    },
    priceAllMax() {
      const machineStore = useMachineStore();
      if (!machineStore.machines.length) return 100000;
      const prices = machineStore.machines.map((m) => +m.custprice || 0);
      return Math.max(...prices);
    },
  },

  actions: {
    // Maschinentyp
    setSelectedMachineType(value) {
      if (value !== this.selectedMachineType) {
        // Abhängige Filter zurücksetzen
        this.selectedTocGroup = "";
        this.selectedEngineType = "";
        this.selectedManufacturer = "";
        setItemWithExpiry("selectedTocGroup", "", TTL_TIME);
        setItemWithExpiry("selectedEngineType", "", TTL_TIME);
        setItemWithExpiry("selectedManufacturer", "", TTL_TIME);
      }
      this.selectedMachineType = value;
      setItemWithExpiry("selectedMachineType", value, TTL_TIME);
    },

    // Gruppennamen (toc_group_l)
    setSelectedTocGroup(value) {
      if (value !== this.selectedTocGroup) {
        // Abhängige Filter zurücksetzen
        this.selectedEngineType = "";
        this.selectedManufacturer = "";
        setItemWithExpiry("selectedEngineType", "", TTL_TIME);
        setItemWithExpiry("selectedManufacturer", "", TTL_TIME);
      }
      this.selectedTocGroup = value;
      setItemWithExpiry("selectedTocGroup", value, TTL_TIME);
    },

    // Antriebsart
    setSelectedEngineType(engineType) {
      this.selectedEngineType = engineType;
      setItemWithExpiry("selectedEngineType", engineType, TTL_TIME);
    },

    // Hersteller
    setSelectedManufacturer(man) {
      this.selectedManufacturer = man;
      setItemWithExpiry("selectedManufacturer", man, TTL_TIME);
    },

    // Kapazität
    setCapacityRange(range) {
      this.capacityRange = range;
      setItemWithExpiry("capacityRange", JSON.stringify(range), TTL_TIME);
    },

    // Baujahr
    setYocRange(range) {
      this.yocRange = range;
      setItemWithExpiry("yocRange", JSON.stringify(range), TTL_TIME);
    },

    // Preis
    setPriceRange(range) {
      this.priceRange = range;
      setItemWithExpiry("priceRange", JSON.stringify(range), TTL_TIME);
    },

    // Reset der Filter
    resetAll() {
      this.selectedMachineType = "";
      this.selectedEngineType = "";
      this.selectedManufacturer = "";
      this.selectedTocGroup = "";
      this.capacityRange = [this.capacityAllMin, this.capacityAllMax];
      this.yocRange = [this.yocAllMin, this.yocAllMax];
      this.priceRange = [this.priceAllMin, this.priceAllMax];

      // Leeren/Zurücksetzen mit neuer TTL
      setItemWithExpiry("selectedMachineType", "", TTL_TIME);
      setItemWithExpiry("selectedEngineType", "", TTL_TIME);
      setItemWithExpiry("selectedManufacturer", "", TTL_TIME);
      setItemWithExpiry("selectedTocGroup", "", TTL_TIME);
      setItemWithExpiry(
        "capacityRange",
        JSON.stringify([this.capacityAllMin, this.capacityAllMax]),
        TTL_TIME
      );
      setItemWithExpiry(
        "yocRange",
        JSON.stringify([this.yocAllMin, this.yocAllMax]),
        TTL_TIME
      );
      setItemWithExpiry(
        "priceRange",
        JSON.stringify([this.priceAllMin, this.priceAllMax]),
        TTL_TIME
      );
    },
  },
});
